// Pure helpers for weekly-board placement — no React, no Firebase, so the
// fiddly parts (ordering, grouping) are testable on their own. See
// placement.test.js.
import { addDaysIso } from './calendarMath';

export const DAY_LABELS = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri' };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2026-09-08" -> "Sep 8". Day columns lead with the weekday name, so the
// full ISO string the week view used to print is just noise there.
export function shortDate(dateIso) {
  if (!dateIso) return '';
  const [, m, d] = dateIso.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

// Mon..Fri of the week starting at `monday`.
export function weekDates(monday) {
  return monday ? [0, 1, 2, 3, 4].map(i => addDaysIso(monday, i)) : [];
}

// ─── ORDER ────────────────────────────────────────────────────────────────────
// Where a placed tile sorts within its day. The seed's daily six take orders
// 0-6 (0-7 on Tue/Thu, which carry an extra item), and the clock-bound anchors
// — tutor/swim/co-op — take 900, so the placed band sits between them.
export const PLACED_MIN = 700;
export const PLACED_MAX = 899;

// Max-in-use + 1, NOT "700 + how many items are on this day". The count-based
// version collides as soon as anything is un-placed: put A then B on Monday
// (709, 710), move A to Tuesday, and the next tile placed on Monday counts 10
// again and also gets 710. Two cards then sort by whatever order Firestore
// happened to return them in, and visibly swap for no reason. Since tiles can
// now be freely moved and un-placed, that is a matter of when, not if.
export function nextOrderFor(itemsOnDate, selfId) {
  const used = itemsOnDate
    .filter(a => a.id !== selfId && a.order >= PLACED_MIN && a.order <= PLACED_MAX)
    .map(a => a.order);
  // Math.max() with no args is -Infinity, hence the seed value.
  return Math.min(PLACED_MAX, Math.max(PLACED_MIN - 1, ...used) + 1);
}

// ─── GROUPING ─────────────────────────────────────────────────────────────────
// Both generators emit instance ids of the form `series__{week}__{itemId}__{n}`
// (yearPlanSeed.js's perWeek loop, authoring.js's series builder), so the id
// minus its trailing instance number is a stable per-week group key. itemId
// itself is not stored on the doc, which is why this reads the id.
const SERIES_INSTANCE = /^(series__.+)__(\d+)$/;

export function groupKeyFor(a) {
  const m = SERIES_INSTANCE.exec(a.id || '');
  if (m) return m[1];
  // Hand-authored or CSV-imported unplaced work has no series id — fall back to
  // the fields that would make two rows genuinely interchangeable.
  return `x|${a.courseId}|${a.title}|${a.subLabel || ''}|${a.estMinutes}|${a.block}`;
}

// 17 tiles land in the pool every week (cursive x4, composition x3, digital
// savvy x3, mechanics x2, plus five singles), several of them word-for-word
// identical. Collapsing those to "Cursive copywork x4" turns a 17-row wall into
// 9 rows, and matches the physical board it replaces — a stack of identical
// magnets, not seventeen distinct chores.
//
// Anything the student has already touched is deliberately NOT collapsed: an
// instance that was placed, flagged, then un-placed carries a question and a
// status that would vanish inside a count.
export function groupUnplaced(unplaced) {
  const plain = [];
  const singles = [];
  for (const a of unplaced) {
    const untouched = a.status === 'not_started' && !a.confusionFlag && !a.studentQuestion;
    (untouched ? plain : singles).push(a);
  }

  const map = new Map();
  for (const a of plain) {
    const key = groupKeyFor(a);
    if (!map.has(key)) map.set(key, { key, members: [] });
    map.get(key).members.push(a);
  }

  const groups = [...map.values()].map(g => {
    const members = g.members.slice().sort((x, y) => x.id.localeCompare(y.id));
    return { key: g.key, members, sample: members[0], count: members.length };
  });

  groups.sort(byPlacementDifficulty);
  return { groups, singles };
}

// Hardest-to-place first, so the tiles with real constraints get a day while
// there are still days to choose from. Mechanics (can't use Mon/Wed) and the
// parent-dependent ones surface above cursive copywork, which fits anywhere.
function byPlacementDifficulty(a, b) {
  const constrained = g => (g.sample.avoidDays?.length ? 1 : 0);
  const parented = g => (g.sample.needsParent ? 1 : 0);
  const load = g => (g.sample.estMinutes || 0) * g.count;
  return (
    constrained(b) - constrained(a) ||
    parented(b) - parented(a) ||
    load(b) - load(a) ||
    a.sample.title.localeCompare(b.sample.title)
  );
}

// Total sessions behind a set of groups — the pool reports both numbers
// ("9 kinds of work · 17 sessions"), since the group count alone understates
// the actual Monday workload by half.
export function sessionCount(groups) {
  return groups.reduce((s, g) => s + g.count, 0);
}
